/**
 * Mehndi decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mehndiDecorationsPack: ToilePack = {
    slug: 'mehndi-decorations',
    category: 'occasions',
    name: 'Mehndi decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'floor', label: 'Floor', subject: 'floor seating with bolsters and colourful cushions'},
        {key: 'umbrellas', label: 'Umbrellas', subject: 'a ceiling of hanging colourful umbrellas'},
        {key: 'booth', label: 'Booth', subject: 'a photo booth frame with flowers'},
    ],
    styles: [
        {key: 'boho', label: 'Boho', style: 'boho decoration with macramé, pampas grass, rattan and fairy lights, warm afternoon light from camera-left, a soft golden balance with gentle shadows', palette: 'beige, rust, mustard and cream', framing: 'eye-level wide view of the whole setup'},
        {key: 'rajasthani', label: 'Rajasthani', style: 'bright Rajasthani decoration with bandhani and mirror-work fabrics, marigold strings and brass pots, bright daylight from overhead, a vivid balance of strong colour and short shadows', palette: 'hot pink, orange, yellow and parrot green', framing: 'straight-on symmetric view, centered'},
        {key: 'night', label: 'Night glow', style: 'night decoration with fairy lights, paper lanterns and candles, glow from camera-right and from above, a dark warm balance with glowing points of light', palette: 'green, gold and warm amber', framing: 'three-quarter view, the setup lit against a dark surround'},
    ],
    directions: ['terrace', 'yellow and green', 'sangeet'],
    directionHint: 'try: terrace',
    seo: {
        title: 'Mehndi decoration: floor seating, umbrellas, photo booth',
        metaDescription: 'Nine mehndi decoration pictures for a home or small hall: floor seating, a ceiling of umbrellas and a photo booth in three looks.',
        h1: 'Mehndi decoration: floor seating, umbrella ceiling, photo booth',
        tagline: 'Nine pictures, three mehndi pieces, each in a boho, a Rajasthani and a night-glow look.',
        intro: 'Mehndi decoration for a mehndi function at home or in a small hall: floor seating with bolsters and colourful cushions, a ceiling of hanging coloured umbrellas, and a photo booth framed with flowers. Each piece appears in a boho look of macrame, pampas grass and rattan, a bright Rajasthani look of bandhani fabric and marigold strings, and a night look lit by fairy lights and candles. Type a twist, such as terrace or sangeet, and the same pieces carry it.',
        useCases: [
            'floor seating for a mehndi function',
            'a ceiling of umbrellas over a seating area',
            'a photo booth for guests',
            'a night-lit corner for an evening function',
        ],
        faq: [
            {q: 'What are the three looks?', a: 'Boho, with macrame, pampas grass and rattan; Rajasthani, with bandhani and mirror-work fabrics and marigold strings; and night glow, with fairy lights, paper lanterns and candles.'},
            {q: 'Does the Rajasthani look use bright colours?', a: 'Yes. It uses hot pink, orange, yellow and parrot green, seen straight on.'},
            {q: 'Can I ask for a terrace or a sangeet setup?', a: 'Yes. Type a twist such as terrace or sangeet, and the same three pieces are shown in it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs. There are no people in them.'},
        ],
    },
}
